import { ArrowCounterClockwise, DotsThree, MagnifyingGlass, Plus } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ConversationSummary } from "~/api/conversationTypes";
import {
  useArchiveConversation,
  useDeleteConversation,
  useDocumentConversations,
  useMergeConversations,
  useRecentlyDeletedConversations,
  useRegenerateConversationSummary,
  useRenameConversation,
  useRestoreConversation,
  useTransformConversation,
  useUnarchiveConversation,
} from "~/api/queries";
import { Button } from "~/foundation/components/Button";
import { ConfirmDialog } from "~/foundation/components/Dialog";
import { Empty } from "~/foundation/components/Empty";
import { IconButton } from "~/foundation/components/IconButton";
import { Menu, type MenuItem } from "~/foundation/components/Menu";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { cn } from "~/foundation/utils/cn";
import { formatDateTime } from "~/foundation/utils/time";

/**
 * The conversation index (W8 §B.2).
 *
 * It reads the TURN face, because the lifecycle face's list is owner-filtered — two endpoints,
 * two answers, and a document panel that only showed your own conversations would be quietly
 * wrong. The list is also capped at 50 with no cursor server-side (W8-8), so the count line
 * says "showing the first N" rather than implying there are no more.
 */
export function ConversationList({
  documentId,
  canComment,
  canUpdate,
  activeId,
  onOpen,
  onCreate,
}: {
  documentId: string;
  canComment: boolean;
  canUpdate: boolean;
  activeId: string | null;
  onOpen: (id: string) => void;
  onCreate: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState("");
  const [merging, setMerging] = useState<string[]>([]);
  const [deleting, setDeleting] = useState<ConversationSummary | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);

  const limit = 50;
  const list = useDocumentConversations(documentId, { search, limit }, { poll: true });
  const deleted = useRecentlyDeletedConversations(showDeleted);

  const rename = useRenameConversation();
  const archive = useArchiveConversation();
  const unarchive = useUnarchiveConversation();
  const remove = useDeleteConversation();
  const restore = useRestoreConversation();
  const merge = useMergeConversations();
  const transform = useTransformConversation();
  const regenerate = useRegenerateConversationSummary();

  const rows = list.data ?? [];

  const menuFor = (row: ConversationSummary): MenuItem[] => {
    const items: MenuItem[] = [
      {
        type: "button",
        title: t("Rename"),
        disabled: !canUpdate,
        onClick: () => {
          setRenaming(row.id);
          setRenameDraft(row.title);
        },
      },
      {
        type: "button",
        title: row.isResolved ? t("Unarchive") : t("Archive"),
        disabled: !canUpdate,
        onClick: () => {
          if (row.isResolved) {
            unarchive.mutate(row.id);
          } else {
            archive.mutate(row.id);
          }
        },
      },
      { type: "separator" },
      {
        type: "button",
        title: t("Summarize into a companion document"),
        disabled: !canUpdate,
        onClick: () => transform.mutate({ id: row.id, kind: "Summarize" }),
      },
      {
        type: "button",
        title: t("Simplify into a companion document"),
        disabled: !canUpdate,
        onClick: () => transform.mutate({ id: row.id, kind: "Simplify" }),
      },
      {
        type: "button",
        // An export makes a NEW deliverable and must never overwrite the companion link.
        title: t("Export as a new document"),
        disabled: !canUpdate,
        onClick: () => transform.mutate({ id: row.id, kind: "Summarize", asExport: true }),
      },
      {
        type: "button",
        title: t("Update summary"),
        disabled: !canUpdate || !row.companionDocumentId,
        onClick: () => regenerate.mutate(row.id),
      },
      { type: "separator" },
      {
        type: "button",
        title: merging.includes(row.id) ? t("Remove from merge") : t("Select for merge"),
        disabled: !canUpdate,
        onClick: () =>
          setMerging((current) =>
            current.includes(row.id)
              ? current.filter((entry) => entry !== row.id)
              : [...current, row.id],
          ),
      },
      {
        type: "button",
        title: t("Delete"),
        dangerous: true,
        disabled: !canUpdate,
        onClick: () => setDeleting(row),
      },
    ];
    return items;
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass
            size={14}
            aria-hidden
            className="-translate-y-1/2 absolute top-1/2 start-2 text-text-tertiary"
          />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("Search conversations")}
            aria-label={t("Search conversations")}
            className="h-8 w-full rounded-sm border border-input-border bg-bg ps-7 pe-2 text-body text-text focus:outline-none"
          />
        </div>
        <Button size="sm" icon={Plus} disabled={!canComment} onClick={onCreate}>
          {t("New")}
        </Button>
      </div>

      {merging.length > 0 ? (
        <div className="flex items-center gap-2 rounded-sm border border-divider bg-bg-secondary p-2">
          <Text variant="small">{t("{{count}} selected to merge", { count: merging.length })}</Text>
          <Button
            size="sm"
            className="ms-auto"
            disabled={merging.length < 2 || merge.isPending}
            onClick={() => {
              merge.mutate(
                { ids: merging },
                {
                  onSuccess: () => setMerging([]),
                },
              );
            }}
          >
            {merge.isPending ? t("Merging…") : t("Merge — the originals are kept")}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setMerging([])}>
            {t("Cancel")}
          </Button>
        </div>
      ) : null}

      <div className="scrollbar-thin flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">
        {list.isLoading ? <Spinner label={t("Loading")} /> : null}
        {!list.isLoading && rows.length === 0 ? (
          <Empty message={t("No conversations on this document yet.")} />
        ) : null}

        {rows.map((row) => (
          <div
            key={row.id}
            className={cn(
              "flex items-start gap-2 rounded-sm border p-2",
              row.id === activeId ? "border-accent bg-accent-subtle" : "border-divider bg-bg",
              merging.includes(row.id) ? "ring-1 ring-accent" : null,
            )}
          >
            <button
              type="button"
              onClick={() => onOpen(row.id)}
              className="flex min-w-0 flex-1 flex-col items-start gap-0.5 text-start"
            >
              <span className="w-full truncate text-body text-text">
                {row.title || t("Untitled conversation")}
              </span>
              <span className="text-caption text-text-tertiary">
                {t("{{count}} messages", { count: row.messageCount })}
                {row.updatedAt ? ` · ${formatDateTime(row.updatedAt, i18n.language)}` : ""}
                {row.isResolved ? ` · ${t("Archived")}` : ""}
              </span>
              {row.lastMessagePreview ? (
                <span className="line-clamp-2 w-full text-caption text-text-secondary">
                  {row.lastMessagePreview}
                </span>
              ) : null}
            </button>
            <Menu
              align="end"
              ariaLabel={t("Conversation actions")}
              items={menuFor(row)}
              trigger={<IconButton icon={DotsThree} aria-label={t("Conversation actions")} />}
            />
          </div>
        ))}

        {rows.length >= limit ? (
          <Text variant="small" className="text-text-tertiary">
            {t("Showing the first {{count}} conversations.", { count: limit })}
          </Text>
        ) : null}
      </div>

      <div className="border-t border-divider pt-2">
        <button
          type="button"
          onClick={() => setShowDeleted((value) => !value)}
          className="text-caption text-text-tertiary hover:text-text"
        >
          {showDeleted ? t("Hide recently deleted") : t("Recently deleted")}
        </button>
        {showDeleted ? (
          <ul className="mt-1 flex flex-col gap-1">
            {(deleted.data ?? []).length === 0 ? (
              <li>
                <Text variant="small">{t("Nothing deleted in the last 30 days.")}</Text>
              </li>
            ) : null}
            {(deleted.data ?? []).map((row) => (
              <li key={row.id} className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-caption text-text-secondary">
                  {row.title || t("Untitled conversation")}
                </span>
                <IconButton
                  icon={ArrowCounterClockwise}
                  size="sm"
                  aria-label={t("Restore")}
                  disabled={!canUpdate}
                  onClick={() => restore.mutate(row.id)}
                />
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {renaming ? (
        <div className="flex items-center gap-2 border-t border-divider pt-2">
          <input
            value={renameDraft}
            onChange={(event) => setRenameDraft(event.target.value)}
            aria-label={t("Conversation title")}
            className="h-8 flex-1 rounded-sm border border-input-border bg-bg px-2 text-body text-text focus:outline-none"
          />
          <Button
            size="sm"
            disabled={rename.isPending || renameDraft.trim().length === 0}
            onClick={() => {
              rename.mutate(
                { id: renaming, title: renameDraft.trim() },
                { onSuccess: () => setRenaming(null) },
              );
            }}
          >
            {t("Save")}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setRenaming(null)}>
            {t("Cancel")}
          </Button>
        </div>
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleting(null);
          }
        }}
        title={t("Delete this conversation?")}
        description={t("You can restore it within 30 days.")}
        confirmLabel={t("Delete")}
        cancelLabel={t("Cancel")}
        closeLabel={t("Close")}
        dangerous
        busy={remove.isPending}
        onConfirm={() => {
          if (deleting) {
            remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) });
          }
        }}
      />
    </div>
  );
}
